<template>
  <ButtonCard
    class="button"
    :theme="theme"
    :aspect="aspect"
    stroke="m"
    :direction="direction"
    :selected="selected"
    @click.capture="handleClick"
    @focus="handleMouseOver"
    @blur="handleMouseLeave"
    @mouseover="handleMouseOver"
    @mouseleave="handleMouseLeave"
  >
    <template v-if="iconUrl || iconName" #icon>
      <GraphicMask
        v-if="iconUrl && !iconDoNotForceColorization"
        class="icon"
        :style="style"
        :src="`${iconUrl}`"
        :size="5"
        :color="colorIcon"
        appear-effect="fade-in"
      />
      <GraphicIcon
        v-else-if="iconUrl"
        class="icon"
        :style="style"
        :src="`${iconUrl}`"
        :size="5"
      />
      <icon
        v-else
        class="icon"
        :color="colorIcon"
        :name="iconName"
        size="5rem"
        :style="style"
      />
    </template>

    <span class="name">{{ name }}</span>
  </ButtonCard>
</template>
<script lang="ts" setup>
interface Props {
  name: string;
  value: string;
  iconUrl?: string;
  iconDoNotForceColorization?: boolean;
  iconName?: string;
  selected: boolean;
  theme?: string;
  direction?: 'row' | 'row-reverse' | 'column' | 'column-reverse';
  aspect?:
    | 'default'
    | 'square'
    | 'vertical'
    | '4-3'
    | '14-9'
    | '16-9'
    | 'pano'
    | '14-13';
}
const { $colors } = useNuxtApp();
const props = defineProps({
  name: null,
  value: null,
  iconUrl: null,
  iconDoNotForceColorization: { type: Boolean, default: false },
  iconName: null,
  selected: { type: Boolean },
  theme: { default: 'primary' },
  direction: { default: 'column' },
  aspect: { default: 'square' }
});
const emit = defineEmits(["select"]);

const scaleOver = ref<number>(1.08);
const scaleOut = ref<number>(1);
const scaleDefault = ref<number>(1);

const spring = computed(() => {
  return { damping: 14, stiffness: 240, mass: 0.96 };
});

const { style, scale } = useScaleEffect(
  1,
  false,
  spring.value.damping,
  spring.value.stiffness,
  spring.value.mass
);

const colorIcon = computed(() => {
  return $colors.primary;
});

const handleClick = (e) => {
  emit('select', props.value);
};

const handleMouseOver = () => {
  scale.value = scaleOver.value;
};

const handleMouseLeave = () => {
  scale.value = scaleDefault.value;
};

watchEffect(() => {
  scaleDefault.value = props.selected ? scaleOver.value : scaleOut.value;
});

const csscolor = computed(() => colorIcon.value);
</script>
<style lang="scss" scoped>
.disabled {
  opacity: 0.5;
  pointer-events: none;
}
.name {
  transition: color 400ms $ease-in-quad;
}
.button {
  transition: border 300ms $ease-out-quad, box-shadow 300ms $ease-in-quad;
  border: var(--stroke_thickness_l) solid var(--color_neutral_50);
  box-shadow: rgb(0 0 0 / 10%) 0 1px 3px 0, rgb(0 0 0 / 6%) 0 1px 2px 0;
  &:focus,
  &:hover {
    transition: border 200ms $ease-out-quad, box-shadow 200ms $ease-out-quad;
    box-shadow: rgb(255 106 14 / 80%) 0 1px 3px 0,
      rgb(255 106 14 / 48%) 0 1px 2px 0;
    .title {
      color: v-bind(csscolor);
    }
  }
  &.selected {
    border: var(--stroke_thickness_l) solid var(--color_primary);
    box-shadow: rgb(0 0 0 / 10%) 0 1px 3px 0, rgb(0 0 0 / 6%) 0 1px 2px 0;
  }
}
.icon {
  position: relative;
}
</style>
